import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import type { ArticleData } from '@/mocks/articles-data';

interface ArticleLayoutProps {
  article: ArticleData;
  relatedArticles?: { title: string; link: string; isInternal: boolean }[];
}

export default function ArticleLayout({ article, relatedArticles }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full min-h-[400px] md:min-h-[460px] flex items-end bg-background-50 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={article.heroImage}
            alt={article.title}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/20 via-background-50/10 to-background-50/60"></div>
        </div>
        <div className="relative z-10 w-full px-6 md:px-12 pb-12 md:pb-16">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
              {article.breadcrumb.map((item, i) => (
                <span key={i} className="flex items-center gap-2">
                  {item.path ? (
                    <Link to={item.path} className="hover:text-primary-500 transition-colors cursor-pointer">{item.label}</Link>
                  ) : (
                    <span className={i === article.breadcrumb.length - 1 ? 'text-primary-500' : 'text-foreground-700'}>{item.label}</span>
                  )}
                  {i < article.breadcrumb.length - 1 && <span>&gt;</span>}
                </span>
              ))}
            </nav>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl leading-relaxed">
              {article.summary}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                </div>
                <div>
                  <p className="font-semibold text-foreground-800">{article.author}</p>
                  <p>{article.authorRole}</p>
                </div>
              </div>
              <span className="text-foreground-300">|</span>
              <span>Ultimo aggiornamento: {article.lastUpdated}</span>
              <span className="text-foreground-300">|</span>
              <span>{article.readingTime}</span>
            </div>
          </div>
        </div>
      </section>

      {/* TL;DR Box */}
      <section className="bg-background-100 py-8 md:py-10">
        <div className="px-6 md:px-12 max-w-3xl mx-auto">
          <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
            <h2 className="font-heading text-lg font-bold text-foreground-900 flex items-center gap-2">
              <i className="ri-flashlight-line w-5 h-5 flex items-center justify-center text-accent-500"></i>
              TL;DR &mdash; In breve
            </h2>
            <p className="mt-3 text-sm text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.tldr }}></p>
          </div>
        </div>
      </section>

      {/* Indice */}
      <section className="bg-background-50 py-6 md:py-8">
        <div className="px-6 md:px-12 max-w-3xl mx-auto">
          <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">
            Indice dei contenuti
          </h2>
          <ul className="space-y-2">
            {article.tocItems.map((item, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-foreground-600">
                <span className="text-primary-500 font-semibold">{i + 1}.</span>
                <a href={`#sezione-${i + 1}`} className="hover:text-primary-500 transition-colors cursor-pointer">{item}</a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Article Body */}
      <article className="bg-background-50 py-8 md:py-12">
        <div className="px-6 md:px-12 max-w-3xl mx-auto space-y-10 md:space-y-14">

          {article.sections.map((section, sIdx) => {
            if (section.title.includes('Domande frequenti') || section.title.includes('FAQ') || (section.paragraphs.length === 0 && !section.showCategoryTable)) {
              return null;
            }

            if (section.title.includes('Conclusione')) {
              return null;
            }

            return (
              <section key={sIdx} id={section.id}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {section.title}
                </h2>
                <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} dangerouslySetInnerHTML={{ __html: p }}></p>
                  ))}

                  {section.listItems && (
                    <ul className="space-y-3 list-none">
                      {section.listItems.map((item, lIdx) => (
                        <li key={lIdx} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                            <i className="ri-check-line w-3 h-3 flex items-center justify-center text-accent-600"></i>
                          </span>
                          <div>
                            <strong className="text-foreground-900">{item.title}:</strong>{' '}
                            <span className="text-foreground-600">{item.desc}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}

                  {section.showCategoryTable && (
                    <div className="mt-5 overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="bg-background-100">
                            <th className="text-left p-3 font-semibold text-foreground-900 rounded-l-lg">Categoria</th>
                            <th className="text-left p-3 font-semibold text-foreground-900">Nome Italiano</th>
                            <th className="text-left p-3 font-semibold text-foreground-900 rounded-r-lg">Accesso</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-background-200">
                          {categories.map((cat, i) => (
                            <tr key={i} className="hover:bg-background-50 transition-colors">
                              <td className="p-3 text-foreground-700">{cat.name}</td>
                              <td className="p-3 text-foreground-700">{cat.nameIt}</td>
                              <td className="p-3">
                                <a
                                  href={cat.url}
                                  target="_blank"
                                  rel="noopener nofollow"
                                  className="text-primary-500 underline underline-offset-2 hover:text-primary-600 text-xs font-medium whitespace-nowrap cursor-pointer"
                                >
                                  Esplora
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {section.images && section.images.length > 0 && (
                  <div className={`mt-6 grid ${section.images.length >= 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'} gap-4`}>
                    {section.images.map((img, imgIdx) => (
                      <img
                        key={imgIdx}
                        src={img}
                        alt={`${section.title} - immagine ${imgIdx + 1}`}
                        className="w-full rounded-lg object-cover"
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}

          {/* FAQ Section */}
          <section id="sezione-faq">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Domande frequenti (FAQ)
            </h2>
            <div className="mt-4 space-y-4">
              {article.faqs.map((faq, i) => (
                <div key={i} className="bg-background-100 rounded-lg p-4 md:p-5">
                  <h3 className="font-semibold text-foreground-900 text-sm md:text-base flex items-start gap-2">
                    <span className="text-primary-500 font-bold flex-shrink-0">Q:</span>
                    {faq.q}
                  </h3>
                  <p className="mt-2 text-foreground-600 text-sm leading-relaxed flex items-start gap-2">
                    <span className="text-accent-500 font-bold flex-shrink-0">A:</span>
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Conclusion */}
          <section id="sezione-conclusione">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Conclusione
            </h2>
            <div className="mt-4 space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              {article.conclusionParagraphs.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: p }}></p>
              ))}
            </div>
          </section>

          {/* Related Articles */}
          {relatedArticles && relatedArticles.length > 0 && (
            <div className="bg-background-100 rounded-lg p-5 md:p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">
                Articoli correlati
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedArticles.map((ra, i) => (
                  ra.isInternal ? (
                    <Link
                      key={i}
                      to={ra.link}
                      className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 bg-background-50 rounded-lg px-4 py-3 transition-colors cursor-pointer"
                    >
                      <i className="ri-article-line w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0"></i>
                      {ra.title}
                    </Link>
                  ) : (
                    <a
                      key={i}
                      href={ra.link}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-500 bg-background-50 rounded-lg px-4 py-3 transition-colors cursor-pointer"
                    >
                      <i className="ri-external-link-line w-4 h-4 flex items-center justify-center text-primary-500 flex-shrink-0"></i>
                      {ra.title}
                    </a>
                  )
                ))}
              </div>
            </div>
          )}

          {/* Author Box */}
          <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20editorial%20portrait%20on%20soft%20cream%20background%2C%20clean%20minimalist%20style%2C%20warm%20natural%20lighting%2C%20friendly%20expression%2C%20European%20editorial%20aesthetic%20with%20warm%20olive%20and%20sand%20undertones&width=100&height=100&seq=author-avatar-v2&orientation=squarish"
                  alt={article.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-900">Scritto da: {article.author}</p>
                <p className="text-xs text-foreground-500">{article.authorRole}</p>
                <p className="text-xs text-foreground-400 mt-1">Ultimo aggiornamento: {article.lastUpdated}</p>
                <p className="text-xs text-foreground-400">Controllato dal team editoriale</p>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center py-8 bg-accent-100/50 rounded-lg">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Pronto a scoprire Itaobuy Spreadsheet?
            </h3>
            <p className="mt-3 text-foreground-600 text-sm max-w-md mx-auto">
              Accedi ora alla piattaforma completa ed esplora tutte le categorie di prodotti disponibili.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Vai alla piattaforma completa
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 border-2 border-primary-500 text-primary-500 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Torna alla Home
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>

        </div>
      </article>

      <Footer />
    </main>
  );
}